import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateUnit, deleteUnit } from '../../api/classes';
import { updateLesson, deleteLesson } from '../../api/lessons';
import styles from './UnitEditModal.module.css';

const formatDateForInput = (d) => {
  if (!d) return '';
  return new Date(d).toISOString().split('T')[0];
};

export default function UnitEditModal({ unit, classId, lessons, onClose, onSaved, onDeleted }) {
  const [activeTab, setActiveTab] = useState('details');
  const [form, setForm] = useState({
    title: unit.title || '',
    description: unit.description || '',
    durationWeeks: unit.durationWeeks || 3,
    startDate: formatDateForInput(unit.startDate),
    endDate: formatDateForInput(unit.endDate),
  });
  const [standardCodes, setStandardCodes] = useState(
    (unit.unitStandards || []).map(us => us.standard.standardCode)
  );
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Track dirtiness
  useEffect(() => {
    const origCodes = (unit.unitStandards || []).map(us => us.standard.standardCode).sort().join(',');
    const currCodes = [...standardCodes].sort().join(',');
    const changed =
      form.title !== (unit.title || '') ||
      form.description !== (unit.description || '') ||
      form.durationWeeks !== (unit.durationWeeks || 3) ||
      form.startDate !== formatDateForInput(unit.startDate) ||
      form.endDate !== formatDateForInput(unit.endDate) ||
      origCodes !== currCodes;
    setDirty(changed);
  }, [form, standardCodes, unit]);

  // Escape to close
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateUnit(classId, unit.id, {
        title: form.title.trim(),
        description: form.description.trim() || null,
        durationWeeks: form.durationWeeks,
        startDate: form.startDate || undefined,
        endDate: form.endDate || undefined,
        standardCodes,
      });
      onSaved();
    } catch {
      setError('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUnit(classId, unit.id);
      onDeleted();
    } catch {
      setError('Failed to delete unit.');
      setShowDeleteConfirm(false);
    }
  };

  const handleUnlinkLesson = async (lessonId) => {
    try {
      await updateLesson(lessonId, { unitId: null });
      onSaved();
    } catch {
      setError('Failed to remove lesson from unit.');
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    try {
      await deleteLesson(lessonId);
      onSaved();
    } catch {
      setError('Failed to delete lesson.');
    }
  };

  const handleRemoveStandard = (code) => {
    setStandardCodes(prev => prev.filter(c => c !== code));
  };

  const handleAddStandard = (code) => {
    if (!standardCodes.includes(code)) {
      setStandardCodes(prev => [...prev, code]);
    }
  };

  const formatDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

  // Sort lessons: scheduled first by date, then drafts
  const sortedLessons = [...lessons].sort((a, b) => {
    if (a.scheduledDate && b.scheduledDate) return new Date(a.scheduledDate) - new Date(b.scheduledDate);
    if (a.scheduledDate) return -1;
    if (b.scheduledDate) return 1;
    return 0;
  });

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>Unit {unit.sequenceOrder}: {unit.title}</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          {['details', 'standards', 'lessons'].map(tab => (
            <button
              key={tab}
              className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className={styles.body}>
          {error && <div className={styles.error}>{error}</div>}

          {activeTab === 'details' && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.label}>Title *</label>
                <input
                  className={styles.input}
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  maxLength={200}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Description</label>
                <textarea
                  className={styles.textarea}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  maxLength={1000}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Duration (weeks)</label>
                <input
                  className={styles.input}
                  type="number"
                  min="1"
                  value={form.durationWeeks}
                  onChange={e => setForm(f => ({ ...f, durationWeeks: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div className={styles.dateRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Start Date</label>
                  <input
                    className={styles.input}
                    type="date"
                    value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>End Date</label>
                  <input
                    className={styles.input}
                    type="date"
                    value={form.endDate}
                    onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                  />
                </div>
              </div>
            </>
          )}

          {activeTab === 'standards' && (
            <StandardsTab
              standardCodes={standardCodes}
              unitStandards={unit.unitStandards || []}
              onRemove={handleRemoveStandard}
              onAdd={handleAddStandard}
              subject={unit.class?.subject}
              gradeLevel={unit.class?.gradeLevel}
            />
          )}

          {activeTab === 'lessons' && (
            <LessonsTab
              lessons={sortedLessons}
              onUnlink={handleUnlinkLesson}
              onDelete={handleDeleteLesson}
              formatDate={formatDate}
            />
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.deleteBtn} onClick={() => setShowDeleteConfirm(true)}>
            Delete Unit
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
            <button className={styles.saveBtn} onClick={handleSave} disabled={!dirty || saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <>
          <div className={styles.confirmOverlay} onClick={() => setShowDeleteConfirm(false)} />
          <div className={styles.confirmDialog}>
            <h3 className={styles.confirmTitle}>Delete Unit?</h3>
            <p className={styles.confirmText}>
              This will remove the unit and unlink all standards. Lessons will NOT be deleted, but will be unlinked from this unit.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.cancelBtn} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className={styles.deleteBtn} onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* --- Standards Tab sub-component --- */
function StandardsTab({ standardCodes, unitStandards, onRemove, onAdd, subject, gradeLevel }) {
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingStandards, setLoadingStandards] = useState(false);

  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (term.length < 2) { setSearchResults([]); return; }
    setLoadingStandards(true);
    try {
      const { default: client } = await import('../../api/client');
      const token = localStorage.getItem('token');
      const response = await client.get('/api/standards', {
        params: { subject, gradeLevel, search: term },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setSearchResults(response.data?.data || []);
    } catch {
      setSearchResults([]);
    } finally {
      setLoadingStandards(false);
    }
  };

  return (
    <>
      {standardCodes.length > 0 ? (
        <div className={styles.standardChips}>
          {standardCodes.map(code => (
            <span key={code} className={styles.standardChip}>
              {code}
              <button className={styles.chipRemove} onClick={() => onRemove(code)} aria-label={`Remove ${code}`}>
                &times;
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className={styles.emptyState}>No standards linked to this unit.</p>
      )}

      {!showSearch ? (
        <button className={styles.addStandardBtn} onClick={() => setShowSearch(true)}>
          + Add Standard
        </button>
      ) : (
        <div className={styles.standardSearch}>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search standards (e.g. 4.NF.1 or fractions)..."
            value={searchTerm}
            onChange={e => handleSearch(e.target.value)}
            autoFocus
          />
          {loadingStandards && <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>Searching...</p>}
          <div className={styles.standardsList}>
            {searchResults
              .filter(s => !standardCodes.includes(s.standardCode))
              .map(s => (
                <div key={s.id} className={styles.standardOption} onClick={() => onAdd(s.standardCode)}>
                  <span className={styles.standardOptionCode}>{s.standardCode}</span>
                  {s.description}
                </div>
              ))}
          </div>
          <button
            className={styles.cancelBtn}
            onClick={() => { setShowSearch(false); setSearchTerm(''); setSearchResults([]); }}
            style={{ marginTop: 8, fontSize: 13 }}
          >
            Done
          </button>
        </div>
      )}
    </>
  );
}

/* --- Lessons Tab sub-component --- */
function LessonsTab({ lessons, onUnlink, onDelete, formatDate }) {
  const [confirmDelete, setConfirmDelete] = useState(null);

  if (lessons.length === 0) {
    return <p className={styles.emptyState}>No lessons in this unit yet.</p>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Title</th>
            <th>Date</th>
            <th>Duration</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {lessons.map(lesson => (
            <tr key={lesson.id}>
              <td>
                <Link to={`/lessons/${lesson.id}/edit`} className={styles.lessonLink}>
                  {lesson.title}
                </Link>
              </td>
              <td>
                {lesson.scheduledDate
                  ? formatDate(lesson.scheduledDate)
                  : <span className={styles.draftBadge}>Draft</span>
                }
              </td>
              <td>{lesson.duration ? `${lesson.duration} min` : '—'}</td>
              <td>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button className={styles.actionBtn} onClick={() => onUnlink(lesson.id)}>
                    Remove
                  </button>
                  {confirmDelete === lesson.id ? (
                    <>
                      <button className={styles.deleteActionBtn} onClick={() => { onDelete(lesson.id); setConfirmDelete(null); }}>
                        Confirm
                      </button>
                      <button className={styles.actionBtn} onClick={() => setConfirmDelete(null)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button className={styles.deleteActionBtn} onClick={() => setConfirmDelete(lesson.id)}>
                      Delete
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
